import type { SessionSnapshot, UiEvent } from "../../server/src/shared-types";
import { getAccessToken, type PublicConfig } from "./auth";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (init.body) headers.set("content-type", "application/json");
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? `Error ${res.status}`);
  }
  return res;
}

export async function fetchConfig(): Promise<PublicConfig> {
  const res = await fetch("/api/config");
  if (!res.ok) throw new ApiError(res.status, "Could not load the configuration");
  return (await res.json()) as PublicConfig;
}

export interface Me {
  oid: string;
  name: string;
  email: string;
}

export const api = {
  me: async () => (await (await request("/api/me")).json()) as Me,

  createSession: async () =>
    ((await (await request("/api/sessions", { method: "POST" })).json()) as { sessionId: string })
      .sessionId,

  snapshot: async (sessionId: string) =>
    (await (await request(`/api/sessions/${sessionId}/events`)).json()) as SessionSnapshot,

  sendMessage: async (sessionId: string, text: string) => {
    await request(`/api/sessions/${sessionId}/messages`, {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  },

  confirm: async (sessionId: string, toolUseId: string, approved: boolean, note?: string) => {
    await request(`/api/sessions/${sessionId}/confirm`, {
      method: "POST",
      body: JSON.stringify({ toolUseId, approved, note }),
    });
  },
};

/**
 * Reads the backend SSE with fetch (EventSource does not send the Authorization header).
 * `onReady` fires on the first ping, once the backend is already following the session.
 * Resolves with `settled` when the backend reports that the session settled (end of turn or
 * waiting for the person) and with `ended` if the stream ends for another reason; rejects on network errors.
 */
export async function streamEvents(
  sessionId: string,
  onEvent: (event: UiEvent) => void,
  onReady: () => void,
  signal: AbortSignal,
): Promise<"settled" | "ended"> {
  let ready = false;
  const res = await request(`/api/sessions/${sessionId}/stream`, {
    headers: { accept: "text/event-stream" },
    signal,
  });
  if (!res.body) return "ended";
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return "ended";
    buffer += value;
    let boundary: number;
    while ((boundary = buffer.indexOf("\n\n")) >= 0) {
      const chunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      let type = "message";
      const data: string[] = [];
      for (const line of chunk.split("\n")) {
        if (line.startsWith("event:")) type = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
      }
      if (type === "ping" && !ready) {
        ready = true;
        onReady();
      } else if (type === "settled") {
        return "settled";
      } else if (type === "message" && data.length > 0) {
        onEvent(JSON.parse(data.join("\n")) as UiEvent);
      }
    }
  }
}
